import { hydrateMetricRecord } from "@/metrics/hydration"
import type { MetricDirectoryEntry } from "@/metrics/registry"
import type { MetricRecord } from "@/metrics/runtime"

import type { ResourceAttribution } from "@/types_api/backend/transport/get_metric_response"
import type { ListMetricsRequest } from "@/types_api/backend/transport/list_metrics_request"
import type { MetricThresholdResource } from "@/types/api/MetricThreshold"
import type { MetricRecordData } from "@/types/metrics/contracts"
import { isLlmJudgeManager } from "@/types/metrics/llm-judge-managers"
import { mergeManagerUnit } from "@/types/metrics/manager-units"

type DirectoryIndex = Record<string, MetricDirectoryEntry>

function resolveIsLlmJudge(
  metricManager: string,
  entry: MetricDirectoryEntry | undefined,
): boolean {
  return entry
    ? entry.evaluator === "llm_judge"
    : isLlmJudgeManager(metricManager)
}

function resolveUnitsMetadata<M extends { units?: string } | null | undefined>(
  row: { metric_manager: string; metric_metadata?: M },
  entry: MetricDirectoryEntry | undefined,
): NonNullable<M> {
  const dbMetadata = (row.metric_metadata ?? {}) as NonNullable<M>
  if (dbMetadata.units) {
    return dbMetadata
  }
  if (entry?.base_unit) {
    return { ...dbMetadata, units: entry.base_unit }
  }
  return mergeManagerUnit(row)
}

type ListMetricsTransportRequest = ListMetricsRequest

interface TransportMetricThresholdResource {
  attribution?: ResourceAttribution | null
  name?: string | null
  comparison_operator?: MetricThresholdResource["comparison_operator"] | null
  target_float_upper?: number | null
  target_float_lower?: number | null
  target_values?: string[] | null
  source?: MetricThresholdResource["source"] | null
  create_time?: string | null
  update_time?: string | null
}

export interface TransportMetricResource {
  attribution?: ResourceAttribution | null
  id: string
  organization_id?: string | null
  metric_name: string
  metric_display_name: string
  metric_metadata?: MetricRecordData["metric_metadata"] | null
  description: string
  metric_manager: string
  locked?: boolean
  category?: string | null
  created_at?: string | null
  created_by?: string | null
  last_updated_at?: string | null
  last_updated_by?: string | null
  output_type?: string | null
  tags?: string[] | null
  organization_threshold?: TransportMetricThresholdResource | null
  dependency_metric_ids?: string[] | null
}

const METRICS_LIST_ORDER_BY = "metric_name"
const METRICS_LIST_PAGE_SIZE = 1000
const DEFAULT_THRESHOLD_SOURCE: MetricThresholdResource["source"] = "manual"

export const METRICS_LIST_TRANSPORT_REQUEST = {
  include_builtin: true,
  order_by: METRICS_LIST_ORDER_BY,
  page_size: METRICS_LIST_PAGE_SIZE,
} satisfies ListMetricsTransportRequest

function normalizeTransportThreshold(
  threshold: TransportMetricResource["organization_threshold"],
): MetricThresholdResource | null {
  if (!threshold?.comparison_operator) {
    return null
  }

  return {
    attribution: threshold.attribution,
    name: threshold.name ?? "",
    comparison_operator: threshold.comparison_operator,
    target_float_upper: threshold.target_float_upper ?? null,
    target_float_lower: threshold.target_float_lower ?? null,
    target_values: threshold.target_values ?? null,
    source: threshold.source ?? DEFAULT_THRESHOLD_SOURCE,
    create_time: threshold.create_time ?? "",
    update_time: threshold.update_time ?? "",
  }
}

export function mapTransportMetricToRecordData(
  metric: TransportMetricResource,
  byManager: DirectoryIndex = {},
): MetricRecordData & { id: string } {
  const directoryEntry = byManager[metric.metric_manager]
  const metricData = {
    attribution: metric.attribution,
    id: metric.id,
    organization_id: metric.organization_id ?? undefined,
    metric_name: metric.metric_name,
    metric_display_name: metric.metric_display_name,
    description: metric.description,
    metric_manager: metric.metric_manager,
    locked: metric.locked ?? metric.organization_id == null,
    category: metric.category ?? undefined,
    created_at: metric.created_at ?? undefined,
    created_by: metric.created_by ?? undefined,
    last_updated_at: metric.last_updated_at ?? undefined,
    last_updated_by: metric.last_updated_by ?? undefined,
    output_type: metric.output_type ?? undefined,
    tags: metric.tags ?? [],
    organization_threshold: normalizeTransportThreshold(
      metric.organization_threshold,
    ),
    dependency_metric_ids: metric.dependency_metric_ids ?? null,
    is_llm_judge: resolveIsLlmJudge(metric.metric_manager, directoryEntry),
  }

  return {
    ...metricData,
    metric_metadata: resolveUnitsMetadata(
      { ...metricData, metric_metadata: metric.metric_metadata ?? {} },
      directoryEntry,
    ),
  }
}

function mapTransportMetricToRecord(
  metric: TransportMetricResource,
  byManager: DirectoryIndex,
): MetricRecord {
  return hydrateMetricRecord(mapTransportMetricToRecordData(metric, byManager))
}

export function mapTransportMetricsToRecords(
  metrics: TransportMetricResource[],
  byManager: DirectoryIndex = {},
): Record<string, MetricRecord> {
  return metrics.reduce<Record<string, MetricRecord>>((accumulator, metric) => {
    const existingMetric = accumulator[metric.metric_name]
    if (!existingMetric || metric.organization_id) {
      accumulator[metric.metric_name] = mapTransportMetricToRecord(
        metric,
        byManager,
      )
    }
    return accumulator
  }, {})
}
